import React from "react";
import { Box } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { useConsultation } from "../../ConsultationContext";

type Props = {
  size?: number;
  showSubtitle?: boolean;
  subtitleOverride?: React.ReactNode;
  bioAgeOverride?: number | null;
  chronologicalOverride?: number | null;
  /**
   * Compact variant for small embeddings (stepper chip, etc.). Hides the
   * "Bio Age" text label inside the circle and bumps the number font so it
   * still reads at sizes like 32px.
   */
  compact?: boolean;
};

/**
 * Biological-age clock used as the visual anchor across all Section 1
 * sub-steps. Mirrors the lifespan-ring logic of `BioAgeShareCard` (the
 * QR-share card in `src/modules/share/components/cards/`) so both surfaces
 * communicate the same shape.
 *
 * Visual reference: Figma node 14711:145941; three-state variants at
 *   - 14478:51298 (equal)    — blue arc + dot at end (no overlay)
 *   - 14473:50064 (younger)  — blue arc 0→bio + green overlay bio→chrono + dot at bio
 *   - 14473:50063 (older)    — blue arc 0→bio (covers chrono) + red overlay chrono→bio + dot at bio
 *
 * Mapping (age → arc length):
 *   age 0  → 0°
 *   age 84 → 360°
 *   so any age maps to clamp(age / 84, 0, 1) of the full circle. (84 matches
 *   the share card's `maxAge`.)
 *
 * Anatomy:
 *   - Light gray track ring (full circle).
 *   - Primary-blue arc whose length = bio age mapping (0 → bio).
 *   - When bio !== chrono, a coloured overlay segment covers the real-length
 *     |bio − chrono| range, drawn between min(bio, chrono) and max(bio, chrono):
 *       green when bio < chrono (segment sits past bio, ending at chrono)
 *       red   when bio > chrono (segment sits past chrono, ending at bio)
 *   - Glowing dot anchored at bio age (= end of the blue arc):
 *       no halo when bio === chrono
 *       coloured halo when bio !== chrono
 */
const MAX_AGE = 84;

const ageToRatio = (age: number): number =>
  Math.max(0, Math.min(1, age / MAX_AGE));

/**
 * Pure presentational variant of the bio-age clock — takes the data via
 * props (no consultation context coupling) so it can be reused on the
 * dashboard's `AgeClock` widget and any future surface. The default
 * `BioAgeClockCard` export below is a thin wrapper that reads the
 * consultation context and delegates here.
 */
export type BioAgeClockFigureProps = {
  biological: number;
  chronological: number;
  size?: number;
  showSubtitle?: boolean;
  subtitleOverride?: React.ReactNode;
  /** Optional label override for the inside-circle caption (defaults to "Bio Age"). */
  innerLabel?: string;
  /** Compact variant for small embeddings (stepper chip, etc.). */
  compact?: boolean;
};

export const BioAgeClockFigure: React.FC<BioAgeClockFigureProps> = ({
  biological,
  chronological,
  size = 160,
  showSubtitle = true,
  subtitleOverride,
  innerLabel = "PNOĒ age",
  compact = false,
}) => {
  const theme: Theme = useTheme();
  const display = biological ?? 0;
  const chrono = chronological ?? 0;

  const isYounger = display < chrono;
  const isOlder = display > chrono;
  const isEqual = display === chrono && display > 0;

  // Geometry — stroke + dot radii scale with size so the clock keeps its
  // proportions at any size (160px in Section 1, 32px in the stepper chip).
  const stroke = Math.max(2, Math.round(size * 0.05));
  const innerDotR = Math.max(2, size * 0.025);
  const haloDotR = Math.max(3, size * 0.0375);
  const cx = size / 2;
  const cy = size / 2;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;

  // Lengths — match the share card. Blue arc covers 0→bio; the diff overlay
  // covers the real range |bio − chrono| at offset min(bio, chrono).
  const bioLen = circumference * ageToRatio(display);
  const chronoLen = circumference * ageToRatio(chrono);
  const minAgeLen = Math.min(bioLen, chronoLen);
  const diffLen = Math.abs(chronoLen - bioLen);

  const tipColor = isOlder
    ? theme.colorTokens.D[60]
    : theme.colors_ds.SUCCESS_ICON_MAIN;
  const dotColor = isEqual ? theme.colors_ds.SUCCESS_ICON_MAIN : tipColor;

  // Rotate so the arc starts at the top (12 o'clock) and runs clockwise.
  const rotation = -90;

  // Dot anchored at bio age (= end of the blue arc).
  const dotRatio = ageToRatio(display);
  const dotAngle = ((rotation + dotRatio * 360) * Math.PI) / 180;
  const dotX = cx + radius * Math.cos(dotAngle);
  const dotY = cy + radius * Math.sin(dotAngle);
  const showDot = bioLen > 0;
  const showHalo = !isEqual && (isYounger || isOlder);

  return (
    <Box sx={styles.wrap}>
      <Box sx={{ position: "relative", width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          style={{ display: "block" }}
        >
          <defs>
            <filter
              id="bioAgeDotGlow"
              x="-200%"
              y="-200%"
              width="500%"
              height="500%"
            >
              <feGaussianBlur stdDeviation="3" />
            </filter>
          </defs>

          {/* Background track */}
          <circle
            cx={cx}
            cy={cy}
            r={radius}
            stroke={theme.colorTokens.N[40]}
            strokeWidth={stroke}
            fill="none"
          />

          {/* Main blue arc — bio age length (0 → bio). */}
          {bioLen > 0 && (
            <circle
              cx={cx}
              cy={cy}
              r={radius}
              stroke={theme.colors_ds.PRIMARY_TEXT_MAIN}
              strokeWidth={stroke}
              fill="none"
              strokeLinecap="round"
              strokeDasharray={`${bioLen} ${circumference}`}
              transform={`rotate(${rotation} ${cx} ${cy})`}
            />
          )}

          {/* Difference overlay — real-length segment between bio and chrono.
              Drawn slightly thicker so it stays visible when it overlaps the
              blue arc in the older case (where chrono < bio). */}
          {!isEqual && diffLen > 0 && (
            <circle
              cx={cx}
              cy={cy}
              r={radius}
              stroke={tipColor}
              strokeWidth={stroke + 1}
              fill="none"
              strokeLinecap="round"
              strokeDasharray={`${diffLen} ${circumference}`}
              strokeDashoffset={-minAgeLen}
              transform={`rotate(${rotation} ${cx} ${cy})`}
            />
          )}

          {showDot && (
            <>
              {showHalo && (
                <circle
                  cx={dotX}
                  cy={dotY}
                  r={haloDotR}
                  fill={dotColor}
                  opacity={0.5}
                  filter="url(#bioAgeDotGlow)"
                />
              )}
              <circle
                cx={dotX}
                cy={dotY}
                r={innerDotR}
                fill={theme.colors_ds.NEUTRAL_BG_MAIN}
                stroke={dotColor}
                strokeWidth={Math.max(1, stroke * 0.1875)}
              />
            </>
          )}
        </svg>

        <Box sx={styles.center}>
          <Box
            sx={{
              ...styles.value(theme),
              fontSize: compact ? `${Math.round(size * 0.31)}px` : "24px",
            }}
          >
            {display}
          </Box>
          {!compact && <Box sx={styles.label(theme)}>{innerLabel}</Box>}
        </Box>
      </Box>
      {showSubtitle && (
        <Box sx={styles.subtitle(theme)}>
          {subtitleOverride ?? (chrono ? `Age ${chrono}` : null)}
        </Box>
      )}
    </Box>
  );
};

/**
 * Consultation-bound wrapper — reads `bioAge` from the consultation context
 * and passes it (plus any per-call overrides) to `BioAgeClockFigure`. Exists
 * so existing consultation callers keep their `<BioAgeClockCard />` usage
 * unchanged after the split; non-consultation surfaces should import
 * `BioAgeClockFigure` directly and supply their own data.
 */
const BioAgeClockCard: React.FC<Props> = ({
  size = 160,
  showSubtitle = true,
  subtitleOverride,
  bioAgeOverride,
  chronologicalOverride,
  compact = false,
}) => {
  const { data } = useConsultation();
  const { biological, chronological } = data.bioAge;
  const display =
    bioAgeOverride !== undefined ? bioAgeOverride ?? 0 : biological ?? 0;
  const chrono =
    chronologicalOverride !== undefined
      ? chronologicalOverride ?? 0
      : chronological ?? 0;

  return (
    <BioAgeClockFigure
      biological={display}
      chronological={chrono}
      size={size}
      showSubtitle={showSubtitle}
      subtitleOverride={subtitleOverride}
      compact={compact}
    />
  );
};

export default BioAgeClockCard;

const styles = {
  wrap: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: "8px",
  },
  center: {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    textAlign: "center" as const,
  },
  value: (theme: Theme) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "24px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    lineHeight: 1.4,
  }),
  label: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    lineHeight: 1.4,
    whiteSpace: "nowrap" as const,
  }),
  subtitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
  }),
};
